import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardStat from 'components/CardStat';
import DiagramSemiCircle, { DiagramData } from 'components/DiagramSemiCircle';
import Rating from 'images/icons/Rating.svg?react';
import { useDateRangeStore } from 'stores/dateRange';
import { useContactsQuery } from 'core/api/contacts';
import { isDateInDateRange, substractNbOfDay } from 'utils/dateTimeFormat';
import { computeProgression } from 'core/useCases/dashboard/utils';
import { useDashboardTresholds } from 'core/api/dashboardTresholds';

const NewContactDiagram: FC = () => {
    const { t } = useTranslation();
    const dateRangeSelected = useDateRangeStore((s) => s.dateRange);
    const { data: contactsData } = useContactsQuery();
    const contacts = useMemo(() => contactsData ?? [], [contactsData]);
    const tresholds = useDashboardTresholds();
    const [value, setValue] = useState<number>();
    const [progression, setProgression] = useState<string>('');

    useEffect(() => {
        const total = contacts.filter((contact) =>
            isDateInDateRange(new Date(contact.createdDate), dateRangeSelected),
        ).length;

        const { startDate, endDate } = dateRangeSelected;
        const previousWeekDateRange = {
            endDate: substractNbOfDay(endDate, 7),
            startDate: substractNbOfDay(startDate, 7),
        };
        const previousTotal = contacts.filter((contact) =>
            isDateInDateRange(new Date(contact.createdDate), previousWeekDateRange),
        ).length;

        setValue(total);
        // Dont display progression if one value are null
        setProgression(previousTotal && total ? computeProgression(previousTotal, total) : '');
    }, [contacts, dateRangeSelected]);

    // Color in theme ?
    const data: DiagramData[] = tresholds
        ? [
              {
                  color: '#FFCEC6',
                  key: 'low',
                  rangeMax: tresholds.newContactLow,
                  rangeMin: 0,
              },
              {
                  color: '#FF9D8E',
                  key: 'middle',
                  rangeMax: tresholds.newContactMedium,
                  rangeMin: tresholds.newContactLow,
              },
              {
                  color: '#FF7F6B',
                  key: 'high',
                  rangeMax: tresholds.newContactHigh,
                  rangeMin: tresholds.newContactMedium,
              },
          ]
        : [];

    return value !== undefined ? (
        <CardStat
            icon={<Rating />}
            progression={progression}
            title={t('diagram.new-contact.title')}
            value={value}
        >
            <DiagramSemiCircle data={data} value={value} />
        </CardStat>
    ) : null;
};

export default NewContactDiagram;
